/**
 * Envoltura de mensajes JSON del netcode (M1-infra). DOM-free.
 *
 * Es exactamente lo que viaja por el WebSocket: el cliente manda inputs, el
 * servidor manda un "welcome" (slot asignado) y snapshots. `GameRoom`
 * (room.ts) produce/consume estos mensajes; el adaptador de socket
 * (Bun.serve o Colyseus) solo los serializa y enruta.
 */
import { InputCommand, Snapshot, RoomPhase } from "./protocol";
import { TeamId } from "../game";
import { parseClientMessage } from "./guard";

/** Fases del ciclo de vida de una sala. Definida en `protocol.ts` (el
 *  snapshot la lleva); se reexporta aquí para no mover el import de nadie. */
export type { RoomPhase };

/**
 * Acciones de control (todas gateadas a admin en el servidor).
 * - Ciclo de vida: start/pause/resume/finish/restart.
 * - Roster: setTeam (mover jugador de equipo), kick (expulsar), transferAdmin
 *   (ceder el rol admin).
 *
 * El marcador NO se puede tocar: lo lleva la simulación y solo lo mueven los
 * goles. Una partida cuyo resultado puede editar alguien no es una partida.
 */
export type ControlAction =
  | "start" | "pause" | "resume" | "finish" | "restart"
  | "setTeam" | "kick" | "transferAdmin" | "grantAdmin" | "revokeAdmin" | "setTeamNames"
  | "toSpectator" | "toPlayer";

/** Ocupante de un slot de la sala. `team`/`isAdmin`/`playerId` los puebla el
 *  servidor; son opcionales para no romper construcciones legacy (loopback/mock). */
export interface PeerInfo {
  slot: number;
  name: string;
  /** Equipo del jugador (rojo/turquesa) o "spec" si es espectador (sin cuerpo). */
  team?: TeamId | "spec";
  /** true si este ocupante es el admin de la sala (rol server-authoritative). */
  isAdmin?: boolean;
  /** true SOLO para quien creó la sala: manda por encima de los demás admins y
   *  es el único que reparte el rol (ver `GameRoom`). */
  isOwner?: boolean;
  /** Id estable del jugador (para acciones de admin dirigidas). */
  playerId?: string;
  /** RTT del cliente (ms) que este ocupante reportó, para mostrarlo en el roster.
   *  Cada cliente mide su propio ping (`PredictiveClient.pingMs`) y lo reporta con
   *  un mensaje `ping`; el servidor lo difunde aquí. Ausente = aún sin medir. */
  pingMs?: number;
  /**
   * SEC-3 · Identificador opaco de este ocupante, **con alcance de sala**.
   *
   * Existe para que silenciar a alguien sobreviva a que se reconecte (el slot y
   * el `playerId` cambian; esto no). Va derivado con una sal propia de cada
   * sala, así que el MISMO jugador tiene identificadores distintos en salas
   * distintas: sirve para silenciarlo aquí y no para seguirle el rastro por el
   * juego, que es exactamente donde queremos que esté la línea.
   */
  muteId?: string;
  /** true mientras este ocupante tiene el compositor de chat abierto (los tres
   *  puntitos sobre su disco). Lo fija el servidor con el mensaje `typing`. */
  typing?: boolean;
  /**
   * Icono del disco elegido en el perfil (un id corto; el dibujo lo pone el
   * cliente). Viaja en el roster para que TODOS vean el mismo disco: un adorno
   * que solo se ve uno mismo no es una personalización, es un espejo.
   */
  icon?: string;
}

export type ClientMessage =
  | { t: "input"; cmd: InputCommand }
  | {
      t: "control";
      action: ControlAction;
      mapId?: string;
      goalLimit?: number;
      timeLimit?: number;
      /** Slot objetivo para acciones dirigidas (setTeam/kick/transferAdmin). */
      targetSlot?: number;
      /** Equipo destino para setTeam. */
      team?: TeamId | "spec";
      /** Nombres de los dos equipos para setTeamNames (los modera el servidor). */
      teamNames?: [string, string];
    }
  // El cliente reporta su RTT medido (ms) para que el servidor lo difunda en el
  // roster (ping por usuario). Es telemetría: no afecta la simulación.
  | { t: "ping"; ms: number }
  /**
   * Mensaje de chat de sala.
   *
   * Va por el MISMO socket que el juego, y no por un canal aparte, por dos
   * razones: no hay que abrir ni autenticar una segunda conexión, y el servidor
   * ya sabe quién es este cliente y en qué sala está — el cliente no manda su
   * propio nombre ni su slot, que serían suplantables. El texto crudo se modera
   * en el servidor (SEC-2) antes de difundirse.
   */
  | { t: "chat"; text: string }
  /**
   * "Estoy escribiendo" (el compositor de chat está abierto).
   *
   * Solo viaja el BOOLEANO, no lo que se está tecleando: lo segundo sería
   * publicar borradores que nadie ha decidido enviar todavía. El servidor lo
   * pega al roster y lo difunde con los `peers`, así que la sala entera ve los
   * tres puntitos sobre el disco de quien tiene el teclado ocupado — que es
   * justo lo que explica por qué ese jugador lleva dos segundos quieto.
   */
  | { t: "typing"; on: boolean }
  /**
   * Cambio de PERFIL en caliente: nombre visible y/o icono del disco.
   *
   * Existe porque el nombre viajaba solo en la query del socket, así que
   * cambiarlo obligaba a reconectar — es decir, a salirse de la partida para
   * corregir una errata. El servidor lo modera igual que al entrar (SEC-2) y lo
   * difunde en el roster: nadie se renombra a espaldas de la sala.
   */
  | { t: "profile"; name?: string; icon?: string }
  /**
   * SEC-3 · Reporte de un ocupante de la sala.
   *
   * El cliente manda el SLOT, no una identidad: no tiene por qué conocer ninguna
   * (y darle una sería darle algo con lo que seguir a alguien). El servidor
   * traduce el slot al ocupante actual y registra el reporte.
   */
  | { t: "report"; targetSlot: number; reason: ReportReason };

/** Motivo de un reporte (SEC-3). Lista cerrada: un motivo en texto libre sería
 *  un canal de texto no moderado más, y no aporta nada que no aporte esto. */
export type ReportReason = "cheating" | "abuse" | "spam" | "other";

export type ServerMessage =
  | { t: "welcome"; slot: number; tick: number; roomId?: string; mapId?: string; goalLimit?: number; timeLimit?: number; players?: number }
  | { t: "snapshot"; snap: Snapshot }
  // Los nombres de equipo viajan con el estado de sala (no con el snapshot): son
  // configuración de la sala, cambian una vez cada mucho y los ve todo el mundo
  // igual. Meterlos en el snapshot sería repetirlos 20 veces por segundo.
  | { t: "state"; phase: RoomPhase; mapId?: string; goalLimit?: number; timeLimit?: number; teamNames?: [string, string] }
  /**
   * Roster de la sala. Los ESPECTADORES van aparte de `peers` a propósito: no
   * tienen slot ni cuerpo, y colarlos en la misma lista con un slot falso es
   * pedirle a cada consumidor que se acuerde de filtrarlos.
   */
  | { t: "peers"; peers: PeerInfo[]; spectators?: string[]; spectatorCount?: number }
  /**
   * Chat difundido a toda la sala. El emisor lo identifica el SERVIDOR (slot,
   * nombre y equipo salen del roster, no del mensaje del cliente): así nadie
   * puede firmar como otro. `text` ya viene moderado.
   */
  | { t: "chat"; slot: number; name: string; team?: TeamId | "spec"; text: string }
  /**
   * NET-4 · Aviso operativo de la sala/servidor (reinicio, mantenimiento).
   *
   * Existe porque un despliegue no puede parecerse a una caída. Sin esto, tirar
   * el proceso para desplegar deja a todo el mundo con "reconectando…" sin
   * explicación en mitad de un partido, y la mitad de ellos cerrando la pestaña
   * antes de que el servidor nuevo esté arriba. Avisado, el cliente sabe que va
   * a volver y en cuánto.
   */
  | { t: "notice"; kind: NoticeKind; seconds: number; message?: string };

/** Motivo de un aviso operativo (NET-4). */
export type NoticeKind = "shutdown" | "restart" | "maintenance";

export function encode(m: ClientMessage | ServerMessage): string {
  return JSON.stringify(m);
}

/**
 * Decodifica un mensaje de CLIENTE. Delega en el guard (SEC-1): tope de tamaño,
 * JSON válido, y forma/rango de cada campo. Lo que no pasa se descarta en
 * silencio — el servidor nunca ve un mensaje a medio validar.
 */
export function decodeClient(raw: string): ClientMessage | null {
  return parseClientMessage(raw);
}

export function decodeServer(raw: string): ServerMessage | null {
  try {
    const m = JSON.parse(raw) as ServerMessage;
    if (!m || typeof m !== "object") return null;
    if (
      m.t === "welcome" || m.t === "snapshot" || m.t === "state"
      || m.t === "peers" || m.t === "chat" || m.t === "notice"
    ) return m;
    return null;
  } catch {
    return null;
  }
}
